import React, { useState, useEffect, useCallback } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    People as PeopleIcon,
    Business as BusinessIcon,
    CheckCircle as ActiveIcon,
    Cancel as DisabledIcon,
    Refresh as RefreshIcon,
    Storage as StorageIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import plansApi from '../../services/plansApi';
import RetentionSettingsPage from '../settings/components/RetentionSettingsPage';
import { toServerDate } from '../../timezone';

/** Format the byte-count plan limit (maxEventsPerDay is bytes/day now)
 *  for the inline "limits inherited from plan" panel. -1 = unlimited
 *  is handled by the caller. */
function formatBytesLabel(n) {
    const v = Number(n || 0);
    if (v < 0) return -1; // bubble up so caller renders "Unlimited"
    if (v < 1024) return `${v} B`;
    if (v < 1024 ** 2) return `${(v / 1024).toFixed(1)} KB`;
    if (v < 1024 ** 3) return `${(v / 1024 ** 2).toFixed(1)} MB`;
    if (v < 1024 ** 4) return `${(v / 1024 ** 3).toFixed(2)} GB`;
    return `${(v / 1024 ** 4).toFixed(2)} TB`;
}

const planColors = {
    free: 'default',
    pro: 'primary',
    enterprise: 'success',
};

const OperatorPanel = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [organizations, setOrganizations] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, disabled: 0, freePlan: 0, proPlan: 0, enterprisePlan: 0 });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editOrg, setEditOrg] = useState(null);

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [newOrg, setNewOrg] = useState({ slug: '', name: '', plan: 'free' });
    const [adminMode, setAdminMode] = useState('none');
    const [adminUser, setAdminUser] = useState({ username: '', email: '', password: '' });

    // Users dialog
    const [usersOpen, setUsersOpen] = useState(false);
    const [usersOrgId, setUsersOrgId] = useState(null);
    const [usersOrgName, setUsersOrgName] = useState('');
    const [usersOrgSlug, setUsersOrgSlug] = useState('');
    const [orgUsers, setOrgUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // Create user in org
    const [createUserOpen, setCreateUserOpen] = useState(false);
    const [newUserMode, setNewUserMode] = useState('manual');
    const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'ADMIN' });

    // Delete confirmation dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteOrg, setDeleteOrg] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Retention dialog (per-org)
    const [retentionOpen, setRetentionOpen] = useState(false);
    const [retentionOrg, setRetentionOrg] = useState(null);

    // Available plans (dynamic from API)
    const [availablePlans, setAvailablePlans] = useState([]);

    const loadData = useCallback(async () => {
        try {
            // Plan list comes from `plansApi` (which targets
            // /api/v1.0/subscription-plans) — the same source the
            // /operator/subscription-plans editor writes to. The legacy
            // `billingService.getAvailablePlans` hits an unrelated
            // /api/plans table that's never populated, which is why
            // this dropdown was empty.
            const [orgsRes, statsRes, plans] = await Promise.all([
                billingService.listAllOrgs(),
                billingService.getOrgStats(),
                plansApi.list(),
            ]);
            setOrganizations(orgsRes.data || []);
            setStats(statsRes.data || {});
            setAvailablePlans(Array.isArray(plans) ? plans : []);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load organizations' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleToggleEnabled = async (org) => {
        try {
            await billingService.updateOrgById(org.id, { enabled: !org.enabled });
            setMessage({ type: 'success', text: `Organization "${org.name}" ${org.enabled ? 'disabled' : 'enabled'}` });
            loadData();
        } catch {
            setMessage({ type: 'error', text: 'Failed to update organization' });
        }
    };

    // On-demand disk-usage calc — never auto-run (the ClickHouse scan is heavy).
    const [diskCalculating, setDiskCalculating] = useState({}); // orgId -> bool
    const handleCalculateDisk = async (org) => {
        setDiskCalculating((p) => ({ ...p, [org.id]: true }));
        try {
            const res = await billingService.calculateDiskUsage(org.id);
            const { diskBytes, diskCalculatedAt } = res.data || {};
            // Patch the row in place so the column updates without a full reload.
            setOrganizations((prev) => prev.map((o) =>
                o.id === org.id ? { ...o, diskBytes, diskCalculatedAt } : o));
            setMessage({ type: 'success', text: `Disk usage calculated for "${org.name}"` });
        } catch {
            setMessage({ type: 'error', text: `Failed to calculate disk usage for "${org.name}"` });
        } finally {
            setDiskCalculating((p) => ({ ...p, [org.id]: false }));
        }
    };

    const formatBytes = (bytes) => {
        if (bytes == null) return null;
        if (bytes === 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    };

    const handleEditOpen = (org) => {
        setEditOrg({ ...org });
        setEditOpen(true);
    };

    const handleEditSave = async () => {
        if (!editOrg) return;
        try {
            await billingService.updateOrgById(editOrg.id, {
                slug: editOrg.slug,
                name: editOrg.name,
                plan: editOrg.plan,
                enabled: editOrg.enabled,
                maxUsers: editOrg.maxUsers,
                maxAgents: editOrg.maxAgents,
                maxTests: editOrg.maxTests,
                maxEventsPerDay: editOrg.maxEventsPerDay,
            });
            setMessage({ type: 'success', text: 'Organization updated successfully' });
            setEditOpen(false);
            setEditOrg(null);
            loadData();
        } catch {
            setMessage({ type: 'error', text: 'Failed to update organization' });
        }
    };

    const handleCreateOrg = async () => {
        if (!newOrg.slug || !newOrg.name) {
            setMessage({ type: 'error', text: 'Slug and name are required' });
            return;
        }

        const payload = { ...newOrg };

        // Add admin user if a mode is selected
        if (adminMode !== 'none') {
            if (!adminUser.username) {
                setMessage({ type: 'error', text: 'Admin username is required' });
                return;
            }
            if (adminMode === 'invite' && !adminUser.email) {
                setMessage({ type: 'error', text: 'Email is required for invitation' });
                return;
            }
            if (adminMode === 'manual' && (!adminUser.password || adminUser.password.length < 6)) {
                setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
                return;
            }
            payload.adminUser = {
                mode: adminMode,
                username: adminUser.username,
                email: adminUser.email || undefined,
                password: adminMode === 'manual' ? adminUser.password : undefined,
            };
        }

        try {
            const res = await billingService.createOrg(payload);
            let successMsg = `Organization "${newOrg.name}" created`;
            if (res.data?.adminUser) {
                successMsg += ` with admin user "${res.data.adminUser.username}" (${res.data.adminUser.mode})`;
            }
            // Email-disabled is an informational warning (the org + admin WERE
            // created), not a failure — surface it as a warning, not an error.
            if (res.data?.emailDisabled) {
                setMessage({ type: 'warning', text: `${successMsg}. ${res.data.adminUserError}` });
            } else if (res.data?.adminUserError) {
                setMessage({ type: 'warning', text: `${successMsg}. Note: ${res.data.adminUserError}` });
            } else {
                setMessage({ type: 'success', text: successMsg });
            }
            setCreateOpen(false);
            setNewOrg({ slug: '', name: '', plan: 'free' });
            setAdminMode('none');
            setAdminUser({ username: '', email: '', password: '' });
            loadData();
        } catch (err) {
            const errMsg = err.response?.data?.error || 'Failed to create organization';
            setMessage({ type: 'error', text: errMsg });
        }
    };

    const handleViewUsers = async (org) => {
        setUsersOrgId(org.id);
        setUsersOrgName(org.name);
        setUsersOrgSlug(org.slug || '');
        setUsersOpen(true);
        setUsersLoading(true);
        try {
            const res = await billingService.getOrgUsers(org.id);
            setOrgUsers(res.data || []);
        } catch {
            setOrgUsers([]);
        } finally {
            setUsersLoading(false);
        }
    };

    const refreshOrgUsers = async (orgId) => {
        setUsersLoading(true);
        try {
            const res = await billingService.getOrgUsers(orgId);
            setOrgUsers(res.data || []);
        } catch {
            setOrgUsers([]);
        } finally {
            setUsersLoading(false);
        }
    };

    const handleCreateUser = async () => {
        if (!newUser.username) {
            setMessage({ type: 'error', text: 'Username is required' });
            return;
        }
        if (newUserMode === 'invite' && !newUser.email) {
            setMessage({ type: 'error', text: 'Email is required for invitation' });
            return;
        }
        if (newUserMode === 'manual' && (!newUser.password || newUser.password.length < 6)) {
            setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
            return;
        }

        try {
            await billingService.createOrgUser(usersOrgId, {
                mode: newUserMode,
                username: newUser.username,
                email: newUser.email || undefined,
                password: newUserMode === 'manual' ? newUser.password : undefined,
                role: newUser.role,
            });
            setMessage({ type: 'success', text: `User "${newUser.username}" created in ${usersOrgName}` });
            setCreateUserOpen(false);
            setNewUser({ username: '', email: '', password: '', role: 'ADMIN' });
            setNewUserMode('manual');
            refreshOrgUsers(usersOrgId);
            loadData();
        } catch (err) {
            const errMsg = err.response?.data?.error || 'Failed to create user';
            setMessage({ type: 'error', text: errMsg });
        }
    };

    const handleRetentionOpen = (org) => {
        setRetentionOrg(org);
        setRetentionOpen(true);
    };

    const handleDeleteOpen = (org) => {
        setDeleteOrg(org);
        setDeleteConfirmText('');
        setDeleteOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteOrg || deleteConfirmText !== deleteOrg.slug) return;
        try {
            await billingService.deleteOrg(deleteOrg.id);
            setMessage({ type: 'success', text: `Organization "${deleteOrg.name}" and all its data deleted permanently` });
            setDeleteOpen(false);
            setDeleteOrg(null);
            setDeleteConfirmText('');
            loadData();
        } catch (err) {
            const errMsg = err.response?.data?.error || 'Failed to delete organization';
            setMessage({ type: 'error', text: errMsg });
        }
    };

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .substring(0, 100);
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Organization',
            flex: 1,
            minWidth: 180,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {params.row.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            {params.row.slug}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            field: 'plan',
            headerName: 'Plan',
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={(params.value || 'free').toUpperCase()}
                    size="small"
                    color={planColors[params.value] || 'default'}
                />
            ),
        },
        {
            field: 'enabled',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => (
                <Tooltip title={params.value ? 'Click to disable' : 'Click to enable'}>
                    <Switch
                        checked={params.value}
                        onChange={() => handleToggleEnabled(params.row)}
                        size="small"
                        color="success"
                    />
                </Tooltip>
            ),
        },
        {
            field: 'userCount',
            headerName: 'Users',
            width: 80,
            type: 'number',
        },
        {
            field: 'maxAgents',
            headerName: 'Agents Quota',
            width: 110,
            type: 'number',
        },
        {
            field: 'maxTests',
            headerName: 'Tests Quota',
            width: 110,
            type: 'number',
        },
        {
            field: 'maxEventsPerDay',
            headerName: 'Events/Day',
            width: 120,
            type: 'number',
            renderCell: (params) => {
                const val = params.value;
                if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
                if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
                return val;
            },
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 120,
            renderCell: (params) => toServerDate(params.value)?.toLocaleDateString() ?? '-',
        },
        {
            field: 'diskBytes',
            headerName: 'Disk Usage',
            width: 180,
            sortable: true,
            renderCell: (params) => {
                const calculating = !!diskCalculating[params.row.id];
                const formatted = formatBytes(params.row.diskBytes);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ minWidth: 64 }}>
                            {formatted != null ? (
                                <>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                        {formatted}
                                    </Typography>
                                    {params.row.diskCalculatedAt && (
                                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                            {toServerDate(params.row.diskCalculatedAt)?.toLocaleDateString()}
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                    Not calculated
                                </Typography>
                            )}
                        </Box>
                        <Tooltip title={formatted != null ? 'Recalculate disk usage' : 'Calculate disk usage'}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={calculating}
                                    onClick={() => handleCalculateDisk(params.row)}
                                    startIcon={calculating
                                        ? <CircularProgress size={12} />
                                        : <StorageIcon sx={{ fontSize: 14 }} />}
                                    sx={{ minWidth: 0, px: 1, fontSize: 11, textTransform: 'none' }}
                                >
                                    {calculating ? '…' : (formatted != null ? 'Recalc' : 'Calc')}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 190,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditOpen(params.row)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="View Users">
                        <IconButton size="small" onClick={() => handleViewUsers(params.row)}>
                            <PeopleIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Retention Settings">
                        <IconButton size="small" onClick={() => handleRetentionOpen(params.row)}>
                            <StorageIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    {params.row.slug !== 'default' && (
                        <Tooltip title="Delete Organization">
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteOpen(params.row)}
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ];

    const userColumns = [
        { field: 'username', headerName: 'Username', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1 },
        {
            field: 'role',
            headerName: 'Role',
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={params.value === 'ADMIN' ? 'primary' : 'default'}
                />
            ),
        },
        {
            field: 'enabled',
            headerName: 'Active',
            width: 80,
            renderCell: (params) => params.value ?
                <ActiveIcon sx={{ color: 'success.main', fontSize: 20 }} /> :
                <DisabledIcon sx={{ color: 'error.main', fontSize: 20 }} />,
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 120,
            renderCell: (params) => toServerDate(params.value)?.toLocaleDateString() ?? '-',
        },
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <TableSkeleton rows={5} columns={4} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700, mb: 0.5 }}>
                    Organizations
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Manage tenants, quotas, and organization settings
                </Typography>
            </Box>

            {message.text && (
                <Alert
                    severity={message.type}
                    sx={{ mb: 2 }}
                    onClose={() => setMessage({ type: '', text: '' })}
                >
                    {message.text}
                </Alert>
            )}

            {/* Stats Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <StatCard label="Total Organizations" value={stats.total} color={theme.palette.primary.main} theme={theme} />
                <StatCard label="Active" value={stats.active} color={theme.palette.success.main} theme={theme} />
                <StatCard label="Disabled" value={stats.disabled} color={theme.palette.error.main} theme={theme} />
                <StatCard label="Free Plan" value={stats.freePlan} color={theme.palette.text.secondary} theme={theme} />
                <StatCard label="Pro Plan" value={stats.proPlan} color={theme.palette.primary.main} theme={theme} />
                <StatCard label="Enterprise" value={stats.enterprisePlan} color={theme.palette.success.dark} theme={theme} />
            </Box>

            {/* Organizations Table */}
            <Paper
                sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary' }}>
                        Organizations ({organizations.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={loadData}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateOpen(true)}
                        >
                            Create Organization
                        </Button>
                    </Box>
                </Box>

                <DataGrid
                    rows={organizations}
                    columns={columns}
                    autoHeight
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-cell': {
                            borderColor: theme.palette.divider,
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            borderColor: theme.palette.divider,
                            bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: isDark
                                ? 'rgba(255,255,255,0.04)'
                                : 'rgba(0,0,0,0.02)',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderColor: theme.palette.divider,
                        },
                    }}
                />
            </Paper>

            {/* Edit Organization Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Organization</DialogTitle>
                <DialogContent>
                    {editOrg && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <TextField
                                label="Organization Name"
                                value={editOrg.name}
                                onChange={(e) => setEditOrg({ ...editOrg, name: e.target.value })}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Slug"
                                value={editOrg.slug}
                                onChange={(e) => setEditOrg({ ...editOrg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText="URL-friendly identifier (used for login routing)"
                            />
                            <FormControl fullWidth>
                                <InputLabel>Plan</InputLabel>
                                <Select
                                    value={editOrg.plan}
                                    onChange={(e) => setEditOrg({ ...editOrg, plan: e.target.value })}
                                    label="Plan"
                                >
                                    {availablePlans.length > 0 ? availablePlans.map(p => (
                                        <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
                                    )) : (
                                        <MenuItem value="" disabled>No plans configured — create one in Subscription Plans first</MenuItem>
                                    )}
                                </Select>
                            </FormControl>

                            {/* Quota limits live on the plan, not on the
                                organization. Edit the plan in
                                Operator → Subscription Plans and the
                                change applies to every tenant assigned
                                to it. */}
                            {(() => {
                                const sel = availablePlans.find(p => p.code === editOrg.plan);
                                if (!sel) return null;
                                const limits = [
                                    ['Users', sel.maxUsers],
                                    ['Agents', sel.maxAgents],
                                    ['Tests', sel.maxTests],
                                    ['Data/day', formatBytesLabel(sel.maxEventsPerDay)],
                                    ['RUM apps', sel.maxRumApps],
                                ];
                                return (
                                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            Limits inherited from the {sel.name} plan
                                        </Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.75 }}>
                                            {limits.map(([k, v]) => (
                                                <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                                                    <Typography variant="caption" fontWeight={600}>
                                                        {v === -1 ? 'Unlimited' : v}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })()}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSave}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Create Organization Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Organization</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Organization Name"
                            value={newOrg.name}
                            onChange={(e) => {
                                const name = e.target.value;
                                setNewOrg({ ...newOrg, name, slug: generateSlug(name) });
                            }}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Slug"
                            value={newOrg.slug}
                            onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            helperText="URL-friendly identifier (auto-generated from name)"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Plan</InputLabel>
                            <Select
                                value={newOrg.plan}
                                onChange={(e) => setNewOrg({ ...newOrg, plan: e.target.value })}
                                label="Plan"
                            >
                                {availablePlans.length > 0 ? availablePlans.map(p => (
                                    <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
                                )) : (
                                    <MenuItem value="" disabled>No plans configured — create one in Subscription Plans first</MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        {/* Admin User Section */}
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="text.secondary">
                            First Admin User (optional)
                        </Typography>
                        <RadioGroup
                            row
                            value={adminMode}
                            onChange={(e) => {
                                setAdminMode(e.target.value);
                                setAdminUser({ username: '', email: '', password: '' });
                            }}
                        >
                            <FormControlLabel value="none" control={<Radio size="small" />} label="None" />
                            <FormControlLabel value="invite" control={<Radio size="small" />} label="Invite by Email" />
                            <FormControlLabel value="manual" control={<Radio size="small" />} label="Create Manually" />
                        </RadioGroup>

                        {adminMode !== 'none' && (
                            <>
                                <TextField
                                    label="Admin Username"
                                    value={adminUser.username}
                                    onChange={(e) => setAdminUser({ ...adminUser, username: e.target.value })}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    required
                                />
                                <TextField
                                    label="Admin Email"
                                    type="email"
                                    value={adminUser.email}
                                    onChange={(e) => setAdminUser({ ...adminUser, email: e.target.value })}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    required={adminMode === 'invite'}
                                    helperText={adminMode === 'invite' ? 'Invitation will be sent to this email' : 'Optional'}
                                />
                                {adminMode === 'manual' && (
                                    <TextField
                                        label="Admin Password"
                                        type="password"
                                        value={adminUser.password}
                                        onChange={(e) => setAdminUser({ ...adminUser, password: e.target.value })}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        helperText="Minimum 6 characters"
                                    />
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setCreateOpen(false);
                        setAdminMode('none');
                        setAdminUser({ username: '', email: '', password: '' });
                    }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateOrg}
                        disabled={!newOrg.slug || !newOrg.name}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Users Dialog */}
            <Dialog open={usersOpen} onClose={() => setUsersOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Users - {usersOrgName}</span>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateUserOpen(true)}
                    >
                        Create User
                    </Button>
                </DialogTitle>
                <DialogContent>
                    {usersLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <TableSkeleton rows={5} columns={4} />
                        </Box>
                    ) : orgUsers.length === 0 ? (
                        <Typography sx={{ p: 2, color: 'text.secondary' }}>No users in this organization.</Typography>
                    ) : (
                        <DataGrid
                            rows={orgUsers}
                            columns={userColumns}
                            autoHeight
                            pageSize={10}
                            disableRowSelectionOnClick
                            getRowId={(row) => row.id}
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-cell': { borderColor: theme.palette.divider },
                                '& .MuiDataGrid-columnHeaders': { borderColor: theme.palette.divider },
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUsersOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Create User Dialog */}
            <Dialog open={createUserOpen} onClose={() => setCreateUserOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create User - {usersOrgName}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <RadioGroup
                            row
                            value={newUserMode}
                            onChange={(e) => {
                                setNewUserMode(e.target.value);
                                setNewUser({ username: '', email: '', password: '', role: newUser.role });
                            }}
                        >
                            <FormControlLabel value="manual" control={<Radio size="small" />} label="Create Manually" />
                            <FormControlLabel value="invite" control={<Radio size="small" />} label="Invite by Email" />
                        </RadioGroup>
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={newUser.role}
                                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                label="Role"
                            >
                                <MenuItem value="ADMIN">Admin</MenuItem>
                                <MenuItem value="USER">User</MenuItem>
                                {/* WEB_ADMIN ("Website Operator") is a
                                    sub-operator role for VerOps staff
                                    — only meaningful in the default
                                    org since there's only one
                                    operator surface. */}
                                {usersOrgSlug === 'default' && (
                                    <MenuItem value="WEB_ADMIN">Website Operator (WEB_ADMIN)</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Username"
                            value={newUser.username}
                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            required={newUserMode === 'invite'}
                            helperText={newUserMode === 'invite' ? 'Invitation will be sent to this email' : 'Optional'}
                        />
                        {newUserMode === 'manual' && (
                            <TextField
                                label="Password"
                                type="password"
                                value={newUser.password}
                                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                required
                                helperText="Minimum 6 characters"
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setCreateUserOpen(false);
                        setNewUser({ username: '', email: '', password: '', role: 'ADMIN' });
                        setNewUserMode('manual');
                    }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateUser}
                        disabled={!newUser.username}
                    >
                        {newUserMode === 'invite' ? 'Send Invitation' : 'Create User'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Retention Settings Dialog (per-org) */}
            <Dialog
                open={retentionOpen}
                onClose={() => setRetentionOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Retention Settings - {retentionOrg?.name}
                </DialogTitle>
                <DialogContent>
                    {retentionOrg && (
                        <RetentionSettingsPage orgId={retentionOrg.id} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRetentionOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>Delete Organization</DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        This will permanently delete <strong>{deleteOrg?.name}</strong> and ALL its data
                        including users, tests, agents, alerts, dashboards, and settings.
                        This action cannot be undone.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Type <strong>{deleteOrg?.slug}</strong> to confirm:
                    </Typography>
                    <TextField
                        fullWidth
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder={deleteOrg?.slug}
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleteConfirmText !== deleteOrg?.slug}
                    >
                        Delete Permanently
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const StatCard = ({ label, value, color, theme }) => (
    <Paper
        sx={{
            p: 2,
            minWidth: 140,
            flex: '1 1 140px',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderLeft: 3,
            borderLeftColor: color,
        }}
    >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
        <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 700 }}>{value}</Typography>
    </Paper>
);

export default OperatorPanel;
